import { IntlShape, MessageDescriptor } from 'react-intl';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { etaMinutes, renderPartsOf } from './fullVideoProgress';
import { fullVideoProgressMessages } from './fullVideoProgressMessages';

type AssetFields = Pick<
  SerializedKitAsset,
  'themeId' | 'status' | 'stage' | 'renderPartsTotal' | 'renderPartsDone' | 'etaSec'
>;

/** "Waits for your lyric video": one message for each full video type, so each language can write it. */
const WAITS_FOR: Partial<Record<StudioThemeId, MessageDescriptor>> = {
  visualizer: fullVideoProgressMessages.waitsForVisualizer,
  lyricVideo: fullVideoProgressMessages.waitsForLyricVideo,
  musicVideo: fullVideoProgressMessages.waitsForMusicVideo,
};

/** "about 9 min left", or null with no estimate. */
export function etaShortText(intl: IntlShape, etaSec: number | null | undefined): string | null {
  const minutes = etaMinutes(etaSec);
  return minutes === null ? null : intl.formatMessage(fullVideoProgressMessages.etaShort, { minutes });
}

/** "Rendering part 4 of 8", "Joining and checking", or null when the asset does not render in parts. */
export function renderPartText(intl: IntlShape, asset: AssetFields): string | null {
  const parts = renderPartsOf(asset);
  if (parts === null) {
    return null;
  }
  return parts.isJoining
    ? intl.formatMessage(fullVideoProgressMessages.joinNow)
    : intl.formatMessage(fullVideoProgressMessages.renderNow, { part: parts.part, total: parts.total });
}

/**
 * The parts line of the song page card (design s10): "Rendering part 4 of 8 · about 9 min left".
 * Null for an asset that does not render in parts.
 */
export function renderPartsLine(intl: IntlShape, asset: AssetFields): string | null {
  const part = renderPartText(intl, asset);
  if (part === null) {
    return null;
  }
  const eta = etaShortText(intl, asset.etaSec);
  return eta === null ? part : `${part} · ${eta}`;
}

/** "Waits for your lyric video", or null for an asset type that is not a full video. */
export function waitsForText(intl: IntlShape, themeId: StudioThemeId): string | null {
  const message = WAITS_FOR[themeId];
  return message === undefined ? null : intl.formatMessage(message);
}
